import { policyCatalogKeys } from '../../api/policy-catalog-api'
import { entityKeys } from '../../lib/entity-queries'
import { policyOrderKeys } from '../../api/policy-order-api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { policyErrorMessage } from '../../lib/policy-authoring'
import {
  assistantPolicyApi,
  assistantPolicyKeys as policyKeys,
  type AssistantPolicy,
  type AssistantPolicyEdit,
} from '../../api/assistant-policy-api'

const policyRefreshIntervalMs = 3000

export function useAssistantPolicyResult(entityId: string, enabled: boolean) {
  return useQuery({
    queryKey: policyKeys.result(entityId),
    queryFn: () => assistantPolicyApi.result(entityId),
    enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.refreshStatus
      return status === 'Pending' || status === 'Running' ? policyRefreshIntervalMs : false
    },
  })
}

export function useSaveAssistantPolicy(entityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      policy,
      edit,
      operationId,
    }: {
      policy: AssistantPolicy | null
      edit: AssistantPolicyEdit
      operationId: string
    }) =>
      policy
        ? assistantPolicyApi.update(entityId, policy, edit)
        : assistantPolicyApi.create(entityId, edit, operationId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: policyKeys.result(entityId) }),
        queryClient.invalidateQueries({ queryKey: entityKeys.settings(entityId) }),
        queryClient.invalidateQueries({ queryKey: ['assistant-availability'] }),
        queryClient.invalidateQueries({ queryKey: policyCatalogKeys.all(entityId) }),
        queryClient.invalidateQueries({ queryKey: policyOrderKeys.all(entityId) }),
      ])
    },
  })
}

export function useDeleteAssistantPolicy(entityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (policy: Pick<AssistantPolicy, 'id' | 'revision'>) => assistantPolicyApi.delete(entityId, policy),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: policyKeys.result(entityId) }),
        queryClient.invalidateQueries({ queryKey: entityKeys.settings(entityId) }),
        queryClient.invalidateQueries({ queryKey: ['assistant-availability'] }),
        queryClient.invalidateQueries({ queryKey: policyCatalogKeys.all(entityId) }),
        queryClient.invalidateQueries({ queryKey: policyOrderKeys.all(entityId) }),
      ])
    },
  })
}

export function assistantPolicyErrorMessage(
  error: unknown,
  operation: 'save' | 'load' | 'delete' = 'save',
) {
  return policyErrorMessage(error, 'ASSISTANT_POLICY', 'assistant', operation)
}
